import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  MessageSquare, 
  Users, 
  Calendar, 
  TrendingUp, 
  LogOut, 
  Search,
  ChevronRight,
  LayoutDashboard,
  FileText,
  Settings,
  CheckCircle,
  Clock,
  XCircle,
  Plug,
  Send,
  Download,
  Palette,
  HelpCircle,
  BarChart3,
  Sparkles,
  Bell,
  Menu,
  X,
  Phone,
  Mail,
  Flame,
  Target,
  CreditCard,
  AlertTriangle,
  FileSignature,
  FileSpreadsheet,
  ChevronDown,
  BookOpen
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import SetupChecklist from '@/components/SetupChecklist';
import ProductTour from '@/components/ProductTour';
import SMSQueueStatus from '@/components/SMSQueueStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lead Score Badge Component
const LeadScoreBadge = ({ score, grade, compact = false }) => {
  if (score === null || score === undefined) return null;
  
  const gradeConfig = {
    A: { color: 'bg-emerald-500', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50', label: 'Hot' },
    B: { color: 'bg-teal-500', textColor: 'text-teal-700', bgColor: 'bg-teal-50', label: 'Strong' },
    C: { color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50', label: 'Good' },
    D: { color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50', label: 'Fair' },
    F: { color: 'bg-slate-400', textColor: 'text-slate-600', bgColor: 'bg-slate-50', label: 'Low' }
  };
  
  const config = gradeConfig[grade] || gradeConfig.C;
  
  if (compact) {
    return (
      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${config.bgColor}`}>
        {grade === 'A' && <Flame className="w-3 h-3 text-orange-500" />}
        <span className={`text-xs font-bold ${config.textColor}`}>{score}</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center`}>
        <span className="text-white text-xs font-bold">{grade}</span>
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-900">{score}/100</div>
        <div className={`text-xs ${config.textColor}`}>{config.label} Lead</div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status, compact = false }) => {
  const statusConfig = {
    viable: { 
      label: 'High Priority', 
      shortLabel: 'High',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: CheckCircle
    },
    review: { 
      label: 'Needs Review', 
      shortLabel: 'Review',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: Clock
    },
    unlikely: { 
      label: 'Lower Priority', 
      shortLabel: 'Lower',
      className: 'bg-slate-50 text-slate-600 border-slate-200',
      icon: XCircle
    }
  };
  
  const config = statusConfig[status] || statusConfig.review;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-semibold border ${config.className}`}>
      <Icon className="w-3 h-3" />
      <span className="hidden sm:inline">{config.label}</span>
      <span className="sm:hidden">{config.shortLabel}</span>
    </span>
  );
};

// Mobile Lead Card Component
const LeadCard = ({ intake, onClick, formatDate }) => (
  <div 
    className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm active:bg-slate-50 transition-colors"
    onClick={onClick}
    data-testid={`lead-card-${intake.id}`}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        {intake.lead_score !== null && intake.lead_score !== undefined && (
          <LeadScoreBadge score={intake.lead_score} grade={intake.lead_grade} compact />
        )}
        <div>
          <h3 className="font-semibold text-slate-900">{intake.client_name}</h3>
          <p className="text-sm text-slate-500 capitalize">{intake.accident_type.replace(/_/g, ' ')}</p>
        </div>
      </div>
      <StatusBadge status={intake.status} compact />
    </div>
    
    <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
      {intake.client_phone && (
        <div className="flex items-center gap-1">
          <Phone className="w-3.5 h-3.5" />
          <span className="truncate max-w-[100px]">{intake.client_phone}</span>
        </div>
      )}
      {intake.client_email && (
        <div className="flex items-center gap-1">
          <Mail className="w-3.5 h-3.5" />
          <span className="truncate max-w-[120px]">{intake.client_email}</span>
        </div>
      )}
    </div>
    
    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
      <span className="text-xs text-slate-400">{formatDate(intake.submitted_at)}</span>
      <div className="flex items-center gap-2">
        {intake.consultation_scheduled ? (
          <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Scheduled
          </span>
        ) : (
          <span className="text-xs text-slate-400">No consultation</span>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  </div>
);

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [intakes, setIntakes] = useState([]);
  const [stats, setStats] = useState({
    total_intakes: 0,
    viable_count: 0,
    review_count: 0,
    unlikely_count: 0,
    scheduled_count: 0,
    converted_count: 0,
    follow_ups_sent: 0,
    follow_ups_pending: 0
  });
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Product tour state
  const [runTour, setRunTour] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [intakesRes, statsRes, trialRes] = await Promise.all([
        axios.get(`${API}/intakes`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/subscription/trial-status`).catch(() => ({ data: null }))
      ]);
      setIntakes(intakesRes.data);
      setStats(statsRes.data);
      setTrialStatus(trialRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if user has completed the tour
  useEffect(() => {
    const tourCompleted = localStorage.getItem('intakepal_tour_completed');
    if (!tourCompleted) {
      setTimeout(() => setRunTour(true), 1500);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close sidebar when window resizes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Memoize filtered intakes to avoid recalculation on every render
  const filteredIntakes = useMemo(() => intakes.filter(intake => {
    const matchesSearch = intake.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          intake.accident_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || intake.status === statusFilter;
    return matchesSearch && matchesStatus;
  }), [intakes, searchTerm, statusFilter]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // CMS Export formats (25 total)
  const CMS_FORMATS = [
    // Legal-Specific CMS
    { id: 'filevine', name: 'Filevine', category: 'legal' },
    { id: 'clio', name: 'Clio', category: 'legal' },
    { id: 'mycase', name: 'MyCase', category: 'legal' },
    { id: 'practicepanther', name: 'PracticePanther', category: 'legal' },
    { id: 'smokeball', name: 'Smokeball', category: 'legal' },
    { id: 'casepeer', name: 'CASEpeer', category: 'legal' },
    { id: 'litify', name: 'Litify', category: 'legal' },
    { id: 'needles', name: 'Needles', category: 'legal' },
    { id: 'smartadvocate', name: 'Smart Advocate', category: 'legal' },
    { id: 'cloudlex', name: 'CloudLex', category: 'legal' },
    { id: 'lawmatics', name: 'Lawmatics', category: 'legal' },
    { id: 'rocketmatter', name: 'Rocket Matter', category: 'legal' },
    { id: 'cosmolex', name: 'CosmoLex', category: 'legal' },
    { id: 'abacuslaw', name: 'AbacusLaw', category: 'legal' },
    { id: 'pclaw', name: 'PCLaw', category: 'legal' },
    { id: 'tabs3', name: 'Tabs3', category: 'legal' },
    { id: 'timesolv', name: 'TimeSolv', category: 'legal' },
    { id: 'leaplegal', name: 'LEAP Legal', category: 'legal' },
    // Plaintiff-Specific
    { id: 'trialworks', name: 'TrialWorks', category: 'plaintiff' },
    { id: 'prevail', name: 'Prevail', category: 'plaintiff' },
    { id: 'simplyconvert', name: 'SimplyConvert', category: 'plaintiff' },
    // General CRM
    { id: 'salesforce', name: 'Salesforce', category: 'crm' },
    { id: 'hubspot', name: 'HubSpot', category: 'crm' },
    { id: 'zohocrm', name: 'Zoho CRM', category: 'crm' },
    { id: 'monday', name: 'Monday.com', category: 'crm' },
  ];

  const exportBulkIntakes = async (cmsType = null) => {
    try {
      const url = cmsType 
        ? `${API}/export/intakes/bulk?cms_type=${cmsType}`
        : `${API}/export/intakes/bulk`;
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Get filename from header or generate
      const contentDisposition = response.headers['content-disposition'];
      let filename = cmsType 
        ? `IntakePal_Bulk_Export_for_${cmsType}.csv`
        : 'IntakePal_Bulk_Universal_Export.csv';
      
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      // Download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Exported ${intakes.length} intakes` + (cmsType ? ` for ${CMS_FORMATS.find(f => f.id === cmsType)?.name}` : ''));
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export intakes');
    }
  };

  const navLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', active: true },
    { to: '/setup', icon: Sparkles, label: 'Setup Wizard' },
    { to: '/help', icon: BookOpen, label: 'Help Center' },
    { to: '/integrations', icon: Plug, label: 'Integrations' },
    { to: '/embed', icon: FileText, label: 'Embed Code' },
    { to: '/branding', icon: Palette, label: 'Branding' },
    { to: '/questions', icon: HelpCircle, label: 'Questions' },
    { to: '/retainer-settings', icon: FileSignature, label: 'Retainer' },
    { to: '/team', icon: Users, label: 'Team' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/notifications', icon: Bell, label: 'Notifications' },
    { to: '/subscription/manage', icon: CreditCard, label: 'Billing' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 text-slate-300 hover:text-white"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              IntakePal
            </span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {user?.full_name?.charAt(0) || 'A'}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 bottom-0 w-72 lg:w-64 bg-slate-900 text-slate-300 z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:left-0
      `}>
        <div className="p-6 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              IntakePal
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 -mr-2 text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="px-4 space-y-1">
          {navLinks.map((link) => (
            <Link 
              key={link.to}
              to={link.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                link.active 
                  ? 'bg-teal-500/20 text-teal-400' 
                  : 'hover:bg-white/10'
              }`}
              data-testid={`sidebar-${link.label.toLowerCase()}-link`}
              data-tour={link.to === '/setup' ? 'nav-settings' : undefined}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </Link>
          ))}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {user?.full_name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.firm_name}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 lg:space-y-8">
          {/* Header */}
          <div>
            <h1 
              className="text-2xl sm:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Dashboard
            </h1>
            <p className="text-slate-600 mt-1 text-sm sm:text-base">Welcome back, {user?.full_name}</p>
          </div>

          {/* Setup Wizard Banner - Show for new users */}
          {stats.total_intakes === 0 && (
            <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50" data-testid="setup-wizard-banner">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-500 rounded-xl flex items-center justify-center shrink-0">
                      <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-white animate-pulse" />
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Complete Your Setup
                      </h3>
                      <p className="text-slate-600 text-xs sm:text-sm">
                        Connect calendar, customize branding, and install widget.
                      </p>
                    </div>
                  </div>
                  <Link to="/setup" className="w-full sm:w-auto">
                    <Button className="w-full sm:w-auto bg-teal-500 hover:bg-teal-600" data-testid="start-setup-wizard-btn">
                      Start Setup
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Trial Expiration Banner - Show when trial is expiring soon */}
          {trialStatus?.is_trialing && trialStatus?.is_expiring_soon && (
            <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="trial-expiring-banner">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-xl flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Your trial ends in {trialStatus.days_remaining} day{trialStatus.days_remaining !== 1 ? 's' : ''}
                      </h3>
                      <p className="text-slate-600 text-xs sm:text-sm">
                        Add a payment method to keep your leads and automation running.
                      </p>
                    </div>
                  </div>
                  <Link to="/subscription/manage" className="w-full sm:w-auto">
                    <Button className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-payment-btn">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Add Payment Method
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Cards - Scrollable on mobile */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6" data-tour="dashboard-stats">
            <Card className="border-slate-200" data-testid="stat-total-leads">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2 p-3 sm:p-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Total Leads</CardTitle>
                <Users className="h-4 w-4 text-slate-400 hidden sm:block" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.total_intakes}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="stat-viable">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2 p-3 sm:p-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">High Priority</CardTitle>
                <CheckCircle className="h-4 w-4 text-emerald-500 hidden sm:block" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-2xl sm:text-3xl font-bold text-emerald-600">{stats.viable_count}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="stat-scheduled">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2 p-3 sm:p-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Consults</CardTitle>
                <Calendar className="h-4 w-4 text-teal-500 hidden sm:block" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-2xl sm:text-3xl font-bold text-teal-600">{stats.scheduled_count}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 hidden sm:block" data-testid="stat-followups">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2 p-3 sm:p-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Emails Sent</CardTitle>
                <Send className="h-4 w-4 text-purple-500 hidden sm:block" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-2xl sm:text-3xl font-bold text-purple-600">{stats.follow_ups_sent}</div>
                <p className="text-xs text-slate-500 mt-1">{stats.follow_ups_pending} pending</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 hidden sm:block" data-testid="stat-converted">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2 p-3 sm:p-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Converted</CardTitle>
                <TrendingUp className="h-4 w-4 text-blue-500 hidden sm:block" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-2xl sm:text-3xl font-bold text-blue-600">{stats.converted_count}</div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area with Leads and Checklist */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Leads Section - Takes 2/3 on large screens */}
            <div className="lg:col-span-2" data-tour="leads-section">
              <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <CardTitle 
                    className="text-lg sm:text-xl font-bold"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    Recent Leads
                  </CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="hidden sm:flex"
                        data-testid="export-dropdown-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Export
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64 max-h-96 overflow-y-auto">
                      <DropdownMenuLabel>Legal Case Management</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {CMS_FORMATS.filter(c => c.category === 'legal').map((cms) => (
                        <DropdownMenuItem 
                          key={cms.id}
                          onClick={() => exportBulkIntakes(cms.id)}
                          data-testid={`bulk-export-${cms.id}-btn`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {cms.name}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Plaintiff-Specific</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {CMS_FORMATS.filter(c => c.category === 'plaintiff').map((cms) => (
                        <DropdownMenuItem 
                          key={cms.id}
                          onClick={() => exportBulkIntakes(cms.id)}
                          data-testid={`bulk-export-${cms.id}-btn`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {cms.name}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>General CRM</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {CMS_FORMATS.filter(c => c.category === 'crm').map((cms) => (
                        <DropdownMenuItem 
                          key={cms.id}
                          onClick={() => exportBulkIntakes(cms.id)}
                          data-testid={`bulk-export-${cms.id}-btn`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {cms.name}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Universal Format</DropdownMenuLabel>
                      <DropdownMenuItem 
                        onClick={() => exportBulkIntakes(null)}
                        data-testid="bulk-export-universal-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        All Fields (CSV)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search leads..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 bg-slate-50 w-full"
                      data-testid="search-input"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-40 bg-slate-50" data-testid="status-filter">
                      <SelectValue placeholder="Filter status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="viable">High Priority</SelectItem>
                      <SelectItem value="review">Needs Review</SelectItem>
                      <SelectItem value="unlikely">Lower Priority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full spinner" />
                </div>
              ) : filteredIntakes.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No leads found</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Leads will appear here when clients submit the intake form
                  </p>
                </div>
              ) : (
                <>
                  {/* Mobile: Card View */}
                  <div className="lg:hidden space-y-3">
                    {filteredIntakes.map((intake) => (
                      <LeadCard 
                        key={intake.id}
                        intake={intake}
                        onClick={() => navigate(`/leads/${intake.id}`)}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>

                  {/* Desktop: Table View */}
                  <div className="hidden lg:block rounded-lg border border-slate-200 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold w-16">Score</TableHead>
                          <TableHead className="font-semibold">Client</TableHead>
                          <TableHead className="font-semibold">Accident Type</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold">Consultation</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredIntakes.map((intake) => (
                          <TableRow 
                            key={intake.id} 
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate(`/leads/${intake.id}`)}
                            data-testid={`lead-row-${intake.id}`}
                          >
                            <TableCell>
                              <LeadScoreBadge score={intake.lead_score} grade={intake.lead_grade} compact />
                            </TableCell>
                            <TableCell className="font-medium">{intake.client_name}</TableCell>
                            <TableCell className="capitalize">{intake.accident_type?.replace(/_/g, ' ')}</TableCell>
                            <TableCell><StatusBadge status={intake.status} /></TableCell>
                            <TableCell className="text-slate-500">{formatDate(intake.submitted_at)}</TableCell>
                            <TableCell>
                              {intake.consultation_scheduled ? (
                                <span className="text-teal-600 font-medium">Scheduled</span>
                              ) : (
                                <span className="text-slate-400">Not scheduled</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
            </div>

            {/* Setup Checklist - Takes 1/3 on large screens */}
            <div className="lg:col-span-1 space-y-6">
              <SMSQueueStatus />
              <SetupChecklist />
            </div>
          </div>

          {/* Mobile Export Button */}
          <div className="sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export All Leads
                  <ChevronDown className="w-3 h-3 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuLabel>CMS-Specific Format</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {CMS_FORMATS.slice(0, 5).map((cms) => (
                  <DropdownMenuItem 
                    key={cms.id}
                    onClick={() => exportBulkIntakes(cms.id)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {cms.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={() => toast.info('Tap Export on desktop to see all 10 CMS formats')}>
                  <span className="text-slate-400">+ 5 more formats...</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => exportBulkIntakes(null)}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Universal CSV (All Fields)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </main>
      
      {/* Product Tour */}
      <ProductTour 
        run={runTour} 
        onComplete={() => setRunTour(false)} 
      />
    </div>
  );
};

export default DashboardPage;
